import type { ReactNode } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface InfoCardProps {
  icon?: ReactNode;
  title: string;
  titleAddon?: ReactNode;
  subtitle: string;
  description?: string;
  metadata?: ReactNode;
  footer?: ReactNode;
  /** Rendered in the header when `menu` is not provided. Use `menu` OR `statusBadge`, not both. */
  statusBadge?: ReactNode;
  actions?: ReactNode | null;
  /** Rendered in the header. When provided, `statusBadge` is not rendered in the header. */
  menu?: ReactNode;
  onClick?: () => void;
  className?: string;
  "data-testid"?: string;
}

export function InfoCard({
  icon,
  title,
  titleAddon,
  subtitle,
  description,
  metadata,
  footer,
  statusBadge,
  actions,
  menu,
  onClick,
  className,
  "data-testid": testId,
}: InfoCardProps) {
  return (
    <Card
      className={cn("transition-all", onClick && "cursor-pointer hover:shadow-md", className)}
      onClick={onClick}
      data-testid={testId}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-3 mb-6">
          {icon}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-base font-semibold text-foreground truncate">{title}</p>
              {titleAddon}
            </div>
            <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
            {description && (
              <p className="text-xs text-muted-foreground/80 truncate mt-0.5">{description}</p>
            )}
          </div>
          {menu}
          {!menu && statusBadge}
        </div>

        {metadata && (
          <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-xs text-muted-foreground mb-4">
            {metadata}
          </div>
        )}

        {footer && <div className="flex items-center justify-between">{footer}</div>}

        {actions && (
          <div className="flex items-center gap-2 pt-3 border-t border-border/60">{actions}</div>
        )}
      </CardContent>
    </Card>
  );
}
